/**
 * The Department Status KPI strip (UAT §1).
 *
 * In the legacy build these five numbers were computed inline inside the renderer, which
 * made them untestable and, in principle, driftable from the tiles below them. They are
 * model logic, not presentation, so they live here — computed from the same signals that
 * colour the tiles, which is what makes the guide's guarantee ("nothing on this strip can
 * disagree with a tile") structural rather than a matter of care.
 *
 * The counting rules are the legacy ones, kept literal — including the ones that look odd:
 *
 *  - "Healthy" here means "not flagged, not watch, not unassessable", which folds judgment
 *    departments in. The Executive Summary deliberately counts them separately, because it
 *    is answering a different question: how much of the organization is formula-verified.
 *    The difference between the two views is real and intentional.
 *  - Action Points adds Data Invalid departments to Flagged ones. They aren't technically
 *    flagged, but broken inputs are just as urgent as a real gap, for a different reason.
 *  - Coverage Gaps counts only fully-uncovered blocks. Single-point-of-failure blocks are
 *    tracked separately and excluded on purpose.
 */

/**
 * @param {object} S      the hydrated state
 * @param {object} engine the engine bound to that state
 * @param {string} activeDept  a department id, or 'ALL'
 */
export function computeKpiStrip(S, engine, activeDept = 'ALL') {
  const inScope = (deptId) => activeDept === 'ALL' || deptId === activeDept;

  const scoped = S.depts
    .filter((dep) => inScope(dep.id))
    .map((dep) => ({ dep, signal: engine.computeDeptSignal(dep) }));

  const flagged = scoped.filter(({ signal }) => signal.flagged);
  const watch = scoped.filter(({ signal }) => !signal.flagged && signal.watch);
  const notConfigured = scoped.filter(({ signal }) => signal.notConfigured);
  const dataInvalid = scoped.filter(({ signal }) => signal.dataInvalid);
  const healthy = scoped.filter(
    ({ signal }) => !signal.notConfigured && !signal.dataInvalid && !signal.flagged && !signal.watch,
  );

  // Only departments whose gap has actually reached their own action threshold. One below
  // its own threshold is excluded even though its real gap is greater than zero.
  const flaggedGap = scoped.reduce(
    (sum, { signal }) =>
      sum + (signal.kind === 'volume' && signal.flagged && signal.volGap > 0 ? signal.volGap : 0),
    0,
  );

  const coverageGaps = scoped.reduce(
    (sum, { signal }) => sum + (signal.kind === 'coverage' ? signal.covUncovered ?? 0 : 0),
    0,
  );

  // Headcount is a pure roster count — never filtered by whether a department's signal
  // could be computed. A department can be Data Invalid and still have real people in it.
  const active = S.team.filter((e) => !e._builtStale && inScope(e.dept));
  const filled = active.filter((e) => !e.vacant).length;
  const vacant = active.filter((e) => e.vacant).length;
  const openReqs = S.team.filter((e) => engine.isVacancyRecruitable(e) && inScope(e.dept)).length;

  return {
    filled,
    vacant,
    flaggedGap,
    coverageGaps,
    actionPoints: flagged.length + dataInvalid.length,
    openReqs,
    /** Vacancies Built has not approved or funded — reported, never folded into Open Reqs. */
    blockedReqs: vacant - openReqs,
    watchCount: watch.length,
    healthyCount: healthy.length,
    notConfiguredCount: notConfigured.length,
    dataInvalidCount: dataInvalid.length,
    deptCount: scoped.length,
  };
}
